// download.js
// Handles the ダウンロード command from context menu

import { authRequest } from './auth.js';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import streamSaver from 'streamsaver';
import axios from 'axios';
import { getRecursiveFolderInfo } from './rest.js';

// Self-host StreamSaver's mitm page (public/streamsaver/) so no request goes to
// a third party. Only used by the StreamSaver fallback path below.
if (typeof window !== 'undefined') {
  streamSaver.mitm = `${window.location.origin}/streamsaver/mitm.html`;
}

// Number of files fetched from GCS in parallel. Keeps the network pipe busy
// without opening thousands of sockets at once.
const DOWNLOAD_CONCURRENCY = 8;

export const downloadFile = async uuid => {
  const fileResponse = await authRequest({
    method: 'GET',
    url: `/api/datamanage/detail/file/${uuid}`,
  });

  let blobResponse = await axios.get(fileResponse.data['signed_url'], {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([blobResponse.data]));
  const link = document.createElement('a');
  link.href = url;
  let fileName = fileResponse.data.name;
  fileName = fileName === '' ? '無名のファイル' : fileName;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Count all files under a folder tree, for progress reporting.
const countFiles = folderData =>
  folderData.files.length +
  folderData.folders.reduce((sum, sub) => sum + countFiles(sub), 0);

// Assign a unique name within a set of already-used names, mirroring the
// original suffixing scheme: "foo.dcm" -> "foo(1).dcm", "bar" -> "bar(1)".
const uniqueName = (rawName, fallback, used) => {
  const name = rawName === '' ? fallback : rawName;
  if (used[name] === undefined) {
    used[name] = 1;
    return name;
  }
  const suffix = used[name];
  used[name] += 1;
  const parts = name.split('.');
  if (parts.length >= 2) {
    parts[parts.length - 2] += `(${suffix})`;
    return parts.join('.');
  }
  return `${name}(${suffix})`;
};

// Make a name safe to use as a File System Access file/directory name. The API
// throws a TypeError for names containing path separators or other reserved
// characters (e.g. a folder literally named "black/white"), so replace them.
// Only needed for the direct-to-folder download; the zip path tolerates them.
const sanitizeFsName = (rawName, fallback) => {
  // eslint-disable-next-line no-control-regex
  let safe = (rawName || '').replace(/[/\\:*?"<>|\x00-\x1f]/g, '_');
  // Trailing dots/spaces are invalid on Windows; '.'/'..' are reserved.
  safe = safe.replace(/[. ]+$/, '');
  if (safe === '' || safe === '.' || safe === '..') {
    return fallback;
  }
  return safe;
};

// Generate the zip and stream it into a writer-like sink (a File System Access
// FileSystemWritableFileStream or a StreamSaver writer — both expose
// write/close/abort). We never build the whole archive as a single in-memory
// Blob, which fails with "can't construct the Blob" for multi-GB downloads.
// Backpressure keeps memory flat regardless of total size.
const pipeZipToSink = (zip, sink, cancelDownloadRef) =>
  new Promise((resolve, reject) => {
    const zipStream = zip.generateInternalStream({
      type: 'uint8array',
      streamFiles: true,
    });

    zipStream.on('data', chunk => {
      if (cancelDownloadRef && cancelDownloadRef.current) {
        zipStream.pause();
        sink.abort('cancelled');
        resolve();
        return;
      }
      // Backpressure: pause zip generation until the chunk is written to disk.
      zipStream.pause();
      sink
        .write(chunk)
        .then(() => zipStream.resume())
        .catch(reject);
    });
    zipStream.on('error', err => {
      try {
        sink.abort(err);
      } catch (e) {
        /* ignore */
      }
      reject(err);
    });
    zipStream.on('end', () => {
      sink
        .close()
        .then(resolve)
        .catch(reject);
    });
    zipStream.resume();
  });

// Fallback for browsers without any streaming write support: build one Blob.
// May fail for very large archives, but keeps small downloads working.
const saveZipAsBlob = async (zip, fileName) => {
  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, fileName);
};

const fsAccessSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.showSaveFilePicker === 'function';

const streamSaverSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.WritableStream !== 'undefined' &&
  typeof streamSaver.createWriteStream === 'function';

const dirAccessSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.showDirectoryPicker === 'function';

export const downloadFolder = async (
  uuid,
  folderName,
  setProgressValue,
  setSnackbarMessage,
  cancelDownloadRef
) => {
  /* ファイル情報をまとめてダウンロードしてzipにする */

  const zipDisplayName = folderName ? folderName : '無題のフォルダ';
  const zipFileName = `${zipDisplayName}.zip`;

  // Acquire a streaming sink *now*, while the click's user activation is still
  // live (showSaveFilePicker requires it). Preference order:
  //   1. File System Access API — Chrome/Edge, no service worker, most reliable.
  //   2. StreamSaver — other browsers with streaming write support.
  //   3. In-memory Blob — last resort (see saveZipAsBlob).
  let fileHandle = null;
  let ssWriter = null;
  if (fsAccessSupported()) {
    try {
      fileHandle = await window.showSaveFilePicker({
        suggestedName: zipFileName,
        types: [
          {
            description: 'Zip archive',
            accept: { 'application/zip': ['.zip'] },
          },
        ],
      });
    } catch (err) {
      // User dismissed the save dialog: cancel the whole download.
      if (err && err.name === 'AbortError') {
        return;
      }
      console.warn('showSaveFilePicker failed, falling back', err);
      fileHandle = null;
    }
  }
  if (!fileHandle && streamSaverSupported()) {
    try {
      ssWriter = streamSaver.createWriteStream(zipFileName).getWriter();
    } catch (err) {
      console.warn('StreamSaver unavailable, will fall back to Blob', err);
      ssWriter = null;
    }
  }

  try {
    setProgressValue(0);
    const folderResponse = await getRecursiveFolderInfo(uuid);
    const zip = new JSZip();
    let rootName = folderResponse.data.name;

    const totalFiles = countFiles(folderResponse.data);

    // If the folder has no files, return immediately
    if (totalFiles === 0) {
      if (ssWriter) await ssWriter.abort('empty');
      return;
    }

    rootName = rootName === '' ? '無題のフォルダ' : rootName;
    const folder = zip.folder(rootName);
    let doneFiles = 0;

    const downloadAndZipRecursive = async (folderData, zipFolder) => {
      // Pre-assign unique file names up front (synchronously) so parallel
      // fetches within a batch cannot race on the name-dedup bookkeeping.
      const usedFiles = {};
      const fileEntries = folderData.files.map(file => ({
        file,
        fileName: uniqueName(file.name, '無題のファイル', usedFiles),
      }));

      // Fetch files in parallel batches, adding each to the zip as it arrives.
      for (let i = 0; i < fileEntries.length; i += DOWNLOAD_CONCURRENCY) {
        if (cancelDownloadRef.current) {
          console.warn('Download was cancelled, Abort');
          return;
        }
        const batch = fileEntries.slice(i, i + DOWNLOAD_CONCURRENCY);
        await Promise.all(
          batch.map(async ({ file, fileName }) => {
            const blobResponse = await axios.get(file['signed_url'], {
              responseType: 'blob',
            });
            zipFolder.file(fileName, blobResponse.data);
            doneFiles += 1;
            setProgressValue(
              Math.min(99, Math.floor((doneFiles / totalFiles) * 100))
            );
          })
        );
        setSnackbarMessage(
          `ダウンロード中: (${doneFiles}/${totalFiles} ファイル)`
        );
      }

      // Recurse into subfolders (each internally batches its own files).
      const usedFolders = {};
      for (const subFolder of folderData.folders) {
        if (cancelDownloadRef.current) {
          return;
        }
        const subFolderName = uniqueName(
          subFolder.name,
          '無題のフォルダ',
          usedFolders
        );
        const zipFolderChild = zipFolder.folder(subFolderName);
        await downloadAndZipRecursive(subFolder, zipFolderChild);
      }
    };

    await downloadAndZipRecursive(folderResponse.data, folder);

    // If cancelled during fetching, abort any open sink and stop.
    if (cancelDownloadRef.current) {
      console.warn('Cancel zip file generation');
      if (ssWriter) await ssWriter.abort('cancelled');
      return;
    }

    if (fileHandle) {
      const writable = await fileHandle.createWritable();
      await pipeZipToSink(zip, writable, cancelDownloadRef);
    } else if (ssWriter) {
      await pipeZipToSink(zip, ssWriter, cancelDownloadRef);
    } else {
      console.warn('Streaming download not supported, falling back to Blob');
      await saveZipAsBlob(zip, zipFileName);
    }

    if (!cancelDownloadRef.current) {
      setProgressValue(100);
    }
  } catch (err) {
    // Don't leave a half-open download dangling if anything throws.
    if (ssWriter) {
      try {
        await ssWriter.abort(err);
      } catch (abortErr) {
        console.warn('Error aborting download writer', abortErr);
      }
    }
    throw err;
  }
};

// Direct-to-folder download: write each file straight into a user-chosen
// directory (File System Access API), recreating the subfolder structure, with
// no zip stage. Each file streams directly to disk so memory stays flat and
// there's no archive to build. Chrome/Edge only; falls back to the zip download
// (downloadFolder) when showDirectoryPicker is unavailable (e.g. Firefox).
export const downloadFolderDirect = async (
  uuid,
  folderName,
  setProgressValue,
  setSnackbarMessage,
  cancelDownloadRef
) => {
  if (!dirAccessSupported()) {
    return downloadFolder(
      uuid,
      folderName,
      setProgressValue,
      setSnackbarMessage,
      cancelDownloadRef
    );
  }

  // Pick the destination directory *now*, while the click's user activation is
  // still live (showDirectoryPicker requires it).
  let destDir;
  try {
    destDir = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (err) {
    // User dismissed the picker: cancel the whole download.
    if (err && err.name === 'AbortError') {
      return;
    }
    throw err;
  }

  setProgressValue(0);
  const folderResponse = await getRecursiveFolderInfo(uuid);
  const totalFiles = countFiles(folderResponse.data);
  if (totalFiles === 0) {
    return;
  }

  // Create a subfolder named after the downloaded folder so its files don't mix
  // with whatever is already in the chosen directory.
  const rootName = sanitizeFsName(folderResponse.data.name, '無題のフォルダ');
  const rootDir = await destDir.getDirectoryHandle(rootName, { create: true });

  let doneFiles = 0;

  const writeFolderRecursive = async (folderData, dirHandle) => {
    // Pre-assign unique file names up front (synchronously) so parallel writes
    // within a batch cannot race on the name-dedup bookkeeping.
    const usedFiles = {};
    const fileEntries = folderData.files.map(file => ({
      file,
      // Sanitize first, then dedup, so uniqueness holds on the safe names.
      fileName: uniqueName(
        sanitizeFsName(file.name, '無題のファイル'),
        '無題のファイル',
        usedFiles
      ),
    }));

    // Fetch + write files in parallel batches, straight to disk.
    for (let i = 0; i < fileEntries.length; i += DOWNLOAD_CONCURRENCY) {
      if (cancelDownloadRef.current) {
        console.warn('Download was cancelled, Abort');
        return;
      }
      const batch = fileEntries.slice(i, i + DOWNLOAD_CONCURRENCY);
      await Promise.all(
        batch.map(async ({ file, fileName }) => {
          const blobResponse = await axios.get(file['signed_url'], {
            responseType: 'blob',
          });
          const fileHandle = await dirHandle.getFileHandle(fileName, {
            create: true,
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blobResponse.data);
          await writable.close();
          doneFiles += 1;
          setProgressValue(
            Math.min(99, Math.floor((doneFiles / totalFiles) * 100))
          );
        })
      );
      setSnackbarMessage(
        `ダウンロード中: (${doneFiles}/${totalFiles} ファイル)`
      );
    }

    // Recurse into subfolders (each internally batches its own files).
    const usedFolders = {};
    for (const subFolder of folderData.folders) {
      if (cancelDownloadRef.current) {
        return;
      }
      const subFolderName = uniqueName(
        sanitizeFsName(subFolder.name, '無題のフォルダ'),
        '無題のフォルダ',
        usedFolders
      );
      const childDir = await dirHandle.getDirectoryHandle(subFolderName, {
        create: true,
      });
      await writeFolderRecursive(subFolder, childDir);
    }
  };

  await writeFolderRecursive(folderResponse.data, rootDir);

  if (!cancelDownloadRef.current) {
    setProgressValue(100);
  }
};
